/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    ILabeledDropdownOnChangeNotification,
    LabeledDropdown,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

/**
 * The interface for the props for the account statement request modal.
 */
interface IAccountStatementRequestModalProps {
    email: string;
    isModalOpen: boolean;
    fromDateString: string;
    toDateString: string;
    canSubmitRequest: boolean;
    modalHeaderLabel: string;
    sendToEmailLabel: string;
    fromDateLabel: string;
    toDateLabel: string;
    submitRequestLabel: string;
    cancelLabel: string;
    className: string;
    selectedUser: string;
    selectedUserLabel: string;
    toggle(): void;
    onChangeFromDate(newFromDate: string): void;
    onChangeToDate(newToDate: string): void;
    onSubmitRequest(): void;
    modalAriaLabel?: string;
    maxDateString?: string;
    // Accessible recipient emails (self first); a dropdown replaces the locked field when there is more than one.
    recipientEmails?: string[];
    selectedRecipientEmail?: string;
    onChangeRecipientEmail?(email: string): void;
}

export const RequestStatementModal: React.FC<IAccountStatementRequestModalProps> = ({
    className,
    email,
    isModalOpen,
    toggle,
    fromDateString,
    toDateString,
    onChangeFromDate,
    onChangeToDate,
    canSubmitRequest,
    modalHeaderLabel,
    sendToEmailLabel,
    fromDateLabel,
    toDateLabel,
    submitRequestLabel,
    cancelLabel,
    onSubmitRequest,
    selectedUser,
    selectedUserLabel,
    modalAriaLabel,
    maxDateString,
    recipientEmails,
    selectedRecipientEmail,
    onChangeRecipientEmail
}) => {
    const displayedEmail = selectedRecipientEmail || email;
    const hasRecipientChoice = !!recipientEmails && recipientEmails.length > 1 && !!onChangeRecipientEmail;

    const onSelectRecipient = React.useCallback(
        (notification: ILabeledDropdownOnChangeNotification) => onChangeRecipientEmail?.(notification.selectedOption.key),
        [onChangeRecipientEmail]
    );
    const onChangeFromDateEvent = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            onChangeFromDate(value);
        },
        [onChangeFromDate]
    );

    const onChangeToDateEvent = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            onChangeToDate(value);
        },
        [onChangeToDate]
    );

    /**
     * If the onBlur is called on the fromDate and the date has been changed, reset focus on it.
     * @param event - The event that is triggering onBlur.
     */
    const onBlurFromDate = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (ObjectExtensions.isFunction(event.target.focus) && event.target.value !== fromDateString) {
                event.target.focus();
            }
        },
        [fromDateString]
    );

    /**
     * If the onBlur is called on the toDate and the date has been changed, reset focus on it.
     * @param event - The event that is triggering onBlur.
     */
    const onBlurToDate = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (ObjectExtensions.isFunction(event.target.focus) && event.target.value !== toDateString) {
                event.target.focus();
            }
        },
        [toDateString]
    );

    /**
     * Prevents Esc key events on date inputs from propagating to the Modal,
     * so closing the native calendar picker does not also close the dialog.
     */
    const onDateInputKeyUp = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
        }
    }, []);

    return (
        <Modal
            className='msc-modal-input-required'
            isOpen={isModalOpen}
            horizontalPosition='center'
            verticalPosition='center'
            autoFocus
            fade={false}
            toggle={toggle}
            aria-label={modalAriaLabel}
        >
            <ModalHeader toggle={toggle}>{modalHeaderLabel}</ModalHeader>
            <ModalBody>
                {selectedUser && (
                    <div>
                        <label className={`${className}__subheaderFormat`}>{selectedUserLabel}</label>
                        <br />
                        <div className={`${className}__selectedUser`}>{selectedUser}</div>
                    </div>
                )}
                {hasRecipientChoice ? (
                    <LabeledDropdown
                        labelText={sendToEmailLabel}
                        labelClassname={`${className}__subheaderFormat`}
                        dropdownId='sendToEmail'
                        dropdownClassname={`${className}__sendToEmail`}
                        dropdownOptions={recipientEmails!.map(recipient => ({ key: recipient, value: recipient }))}
                        selectedOption={{ key: displayedEmail, value: displayedEmail }}
                        controlledByParent
                        onSelectOption={onSelectRecipient}
                    />
                ) : (
                    <label htmlFor='sendToEmail' className={`${className}__subheaderFormat`}>
                        {sendToEmailLabel}
                    </label>
                )}
                {!hasRecipientChoice && (
                    <div id='sendToEmail' className={`${className}__sendToEmail`}>
                        {displayedEmail}
                    </div>
                )}
                <div>
                    <div className={`${className}__requestStatement__fromDate`}>
                        <label htmlFor='fromDateSelector'>{fromDateLabel}</label>
                        <input
                            id='fromDateSelector'
                            type='date'
                            value={fromDateString}
                            onBlur={onBlurFromDate}
                            onChange={onChangeFromDateEvent}
                            onKeyUp={onDateInputKeyUp}
                            max={toDateString ? toDateString : maxDateString}
                            className={`${className}__dateSelector form-control`}
                        />
                    </div>
                    <div className={`${className}__requestStatement__toDate`}>
                        <label htmlFor='toDateSelector'>{toDateLabel}</label>
                        <input
                            id='toDateSelector'
                            type='date'
                            value={toDateString}
                            onBlur={onBlurToDate}
                            onChange={onChangeToDateEvent}
                            onKeyUp={onDateInputKeyUp}
                            min={fromDateString}
                            max={maxDateString}
                            className={`${className}__dateSelector form-control`}
                        />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button
                    className={`${className}__action__submitRequest`}
                    onClick={onSubmitRequest}
                    disabled={!canSubmitRequest}
                    aria-disabled={!canSubmitRequest}
                >
                    {submitRequestLabel}
                </button>
                {` `}
                <button className={`${className}__action__cancelRequest`} onClick={toggle}>
                    {cancelLabel}
                </button>
            </ModalFooter>
        </Modal>
    );
};
